"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useRef, useEffect } from "react";
import type { NextGenToken } from "@/entities/INextgen";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import useIsMobileScreen from "@/hooks/isMobileScreen";
import { get8KUrl, get16KUrl } from "./NextGenTokenImage";
import Image from "next/image";
import DotLoader from "@/components/dotLoader/DotLoader";
export const MAX_ZOOM_SCALE = 20;
export const MIN_ZOOM_SCALE = 1;
export default function NextGenZoomableImage(props) {
  const $ = _c(34);
  const isMobileScreen = useIsMobileScreen();
  const isMobileDevice = useIsMobileDevice();
  const imgRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dragStart, setDragStart] = useState(null);
  const [objectPosition, setObjectPosition] = useState("50% 50%");
  let t0;
  let t1;
  if ($[0] !== dragStart) {
    t0 = () => {
      const preventDefault = _temp;
      if (dragStart !== null) {
        window.addEventListener("wheel", preventDefault, {
          passive: false
        });
        window.addEventListener("touchmove", preventDefault, {
          passive: false
        });
        document.body.style.overflow = "hidden";
      } else {
        window.removeEventListener("wheel", preventDefault);
        window.removeEventListener("touchmove", preventDefault);
        document.body.style.overflow = "";
      }
      return () => {
        window.removeEventListener("wheel", preventDefault);
        window.removeEventListener("touchmove", preventDefault);
        document.body.style.overflow = "";
      };
    };
    t1 = [dragStart];
    $[0] = dragStart;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] !== props) {
    t2 = () => {
      const handleWheel = e_0 => {
        e_0.preventDefault();
        const imgElement = imgRef.current;
        if (!imgElement) {
          return;
        }
        const rect = imgElement.getBoundingClientRect();
        const cursorX = e_0.clientX - rect.left;
        const cursorY = e_0.clientY - rect.top;
        const scrollDirection = e_0.deltaY > 0 ? 1 : -1;
        let newZoomScale = props.zoom_scale + scrollDirection;
        newZoomScale = Math.max(MIN_ZOOM_SCALE, Math.min(MAX_ZOOM_SCALE, newZoomScale));
        const cursorPercentX = cursorX / rect.width * 100;
        const cursorPercentY = cursorY / rect.height * 100;
        const newObjectPosition = `${cursorPercentX}% ${cursorPercentY}%`;
        props.setZoomScale(newZoomScale);
        setObjectPosition(newObjectPosition);
      };
      const imgElement_0 = imgRef.current;
      imgElement_0?.addEventListener("wheel", handleWheel);
      return () => {
        imgElement_0?.removeEventListener("wheel", handleWheel);
      };
    };
    $[3] = props;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== props.zoom_scale) {
    t3 = [props.zoom_scale];
    $[5] = props.zoom_scale;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  useEffect(t2, t3);
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = e_1 => {
      const clientX = e_1.type === "touchstart" ? (e_1 as React.TouchEvent).touches[0]?.clientX : (e_1 as React.MouseEvent).clientX;
      const clientY = e_1.type === "touchstart" ? (e_1 as React.TouchEvent).touches[0]?.clientY : (e_1 as React.MouseEvent).clientY;
      setDragStart({
        x: clientX,
        y: clientY
      });
      e_1.preventDefault();
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleDragStart = t4;
  let handleDragEnd;
  let handleDragging;
  if ($[8] !== dragStart || $[9] !== props.zoom_scale) {
    handleDragging = e_2 => {
      if (!dragStart || props.zoom_scale === MIN_ZOOM_SCALE) {
        return;
      }
      const clientX_0 = e_2.type === "touchmove" ? (e_2 as React.TouchEvent).touches[0]?.clientX : (e_2 as React.MouseEvent).clientX;
      const clientY_0 = e_2.type === "touchmove" ? (e_2 as React.TouchEvent).touches[0]?.clientY : (e_2 as React.MouseEvent).clientY;
      const deltaX = clientX_0 - dragStart.x;
      const deltaY = clientY_0 - dragStart.y;
      updateObjectPosition(-deltaX, -deltaY);
      e_2.preventDefault();
    };
    let t5;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = () => {
        setDragStart(null);
      };
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    handleDragEnd = t5;
    function updateObjectPosition(deltaX_0, deltaY_0) {
      const img = imgRef.current;
      if (!img) {
        return;
      }
      const baseScalingFactor = 0.1 / props.zoom_scale;
      const xScalingFactor = baseScalingFactor * (img.clientWidth / img.naturalWidth);
      const yScalingFactor = baseScalingFactor * (img.clientHeight / img.naturalHeight);
      const adjustedDeltaX = deltaX_0 * xScalingFactor;
      const adjustedDeltaY = deltaY_0 * yScalingFactor;
      setObjectPosition(prevPosition => {
        const [prevX, prevY] = prevPosition ? prevPosition.split(" ").map(_temp2) : [50, 50];
        let newX = prevX + adjustedDeltaX;
        let newY = prevY + adjustedDeltaY;
        newX = Math.max(0, Math.min(100, newX));
        newY = Math.max(0, Math.min(100, newY));
        return `${newX}% ${newY}%`;
      });
    }
    $[8] = dragStart;
    $[9] = props.zoom_scale;
    $[10] = handleDragEnd;
    $[11] = handleDragging;
  } else {
    handleDragEnd = $[10];
    handleDragging = $[11];
  }
  let t5;
  let t6;
  if ($[13] !== props.zoom_scale) {
    t5 = () => {
      if (props.zoom_scale === MIN_ZOOM_SCALE) {
        const timer = setTimeout(() => {
          setObjectPosition("50% 50%");
        }, 200);
        return () => clearTimeout(timer);
      }
    };
    t6 = [props.zoom_scale];
    $[13] = props.zoom_scale;
    $[14] = t5;
    $[15] = t6;
  } else {
    t5 = $[14];
    t6 = $[15];
  }
  useEffect(t5, t6);
  let t7;
  if ($[16] !== isMobileDevice || $[17] !== props.token.id) {
    t7 = function getImageUrl() {
      if (isMobileDevice) {
        return get8KUrl(props.token.id);
      }
      return get16KUrl(props.token.id);
    };
    $[16] = isMobileDevice;
    $[17] = props.token.id;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const getImageUrl = t7;
  let t8;
  if ($[19] !== props) {
    t8 = function ensureImageRendered() {
      setTimeout(() => {
        props.setZoomScale(MIN_ZOOM_SCALE);
        setTimeout(() => {
          setLoading(false);
          props.onImageLoaded();
        }, 5000);
      }, 8000);
    };
    $[19] = props;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const ensureImageRendered = t8;
  let t10;
  let t9;
  if ($[21] !== dragStart || $[22] !== ensureImageRendered || $[23] !== getImageUrl || $[24] !== handleDragEnd || $[25] !== handleDragging || $[26] !== imageLoaded || $[27] !== isMobileDevice || $[28] !== isMobileScreen || $[29] !== loading || $[30] !== objectPosition || $[31] !== props) {
    t10 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const getContent = function getContent() {
        let height = "85vh";
        if (props.is_fullscreen) {
          height = "100vh";
        } else {
          if (isMobileScreen) {
            height = "55vh";
          }
        }
        let cursor = "default";
        if (props.zoom_scale > MIN_ZOOM_SCALE) {
          if (dragStart) {
            cursor = "grabbing";
          } else {
            cursor = "grab";
          }
        }
        return <span className="d-flex align-items-center justify-content-center" style={{
          height,
          width: "auto",
          maxHeight: props.is_fullscreen ? "100vh" : "85vh",
          maxWidth: "100%",
          overflow: "hidden",
          position: "relative"
        }}>{loading && <span className="d-flex flex-column gap-3 align-items-center justify-content-center" style={{
            position: "absolute",
            top: "0",
            left: "0",
            width: "100%",
            height: "100%",
            zIndex: 2,
            backgroundColor: "rgb(0, 0, 0)"
          }}><span className="d-flex flex-wrap text-center">{isMobileDevice ? "8K" : "16K"} Pebbles are very large</span><span className="d-flex flex-wrap text-center">Chill while we download you into the Pebbles multiverse</span><span className="color-white">{imageLoaded ? <>Almost There <DotLoader /></> : <>Downloading <DotLoader /></>}</span></span>}<Image unoptimized={true} ref={imgRef} priority={true} loading="eager" width="0" height="0" style={{
            display: imageLoaded ? "block" : "none",
            height: "auto",
            width: "auto",
            maxHeight: "100%",
            maxWidth: "100%",
            transition: "transform 0.2s ease-out",
            objectFit: "contain",
            transform: `scale(${props.zoom_scale})`,
            transformOrigin: objectPosition,
            cursor
          }} onLoad={() => {
            props.setZoomScale(MIN_ZOOM_SCALE + 1);
            setImageLoaded(true);
            ensureImageRendered();
          }} onMouseDown={handleDragStart} onTouchStart={handleDragStart} onMouseMove={handleDragging} onTouchMove={handleDragging} onMouseUp={handleDragEnd} onTouchEnd={handleDragEnd} onMouseLeave={handleDragEnd} src={getImageUrl()} alt={props.token.name} onError={_temp3} /></span>;
      };
      if (props.maintain_aspect_ratio) {
        t10 = <span className="d-flex flex-column align-items-center">{getContent()}</span>;
        break bb0;
      }
      t9 = getContent();
    }
    $[21] = dragStart;
    $[22] = ensureImageRendered;
    $[23] = getImageUrl;
    $[24] = handleDragEnd;
    $[25] = handleDragging;
    $[26] = imageLoaded;
    $[27] = isMobileDevice;
    $[28] = isMobileScreen;
    $[29] = loading;
    $[30] = objectPosition;
    $[31] = props;
    $[32] = t10;
    $[33] = t9;
  } else {
    t10 = $[32];
    t9 = $[33];
  }
  if (t10 !== Symbol.for("react.early_return_sentinel")) {
    return t10;
  }
  return t9;
}
function _temp3(e_3) {
  e_3.currentTarget.src = "/fallback-image.jpeg";
}
function _temp2(val) {
  return parseFloat(val);
}
function _temp(e) {
  return e.preventDefault();
}